
import React from 'react';

import curtLogo from "@/assets/curt-logo.png";
import gibletIcon from "@/assets/giblet-construct-icon.png";
import Navbar from "@/components/Navbar";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import DomainElements from "@/components/ispai/DomainElements";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import KnowledgeNetworkStats from "@/components/shared/KnowledgeNetworkStats";
import TeamMembersGrid from "@/components/ispai/TeamMembersGrid";
import ConversationPanel from "@/components/shared/ConversationPanel";
import CommandDemo from "@/components/construction/CommandDemo";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InviteColleagueDialog } from "@/components/InviteColleagueDialog";
import { useCurtNetworkStats } from "@/hooks/useCurtNetworkStats";
import { Loader2 } from "lucide-react";

const curtTeamMembers = [
  { image: "/lovable-uploads/c6192cb3-a63d-49f6-8701-264af46c0c60.png", alt: "Global Modularization Expert" }, 
  { image: "/lovable-uploads/5eb043bf-469d-46bf-abe8-2651aa4739b3.png", alt: "GSA Knowledge Network Ambassador" }, 
  { image: "/lovable-uploads/4cbc09c3-250e-47f9-be69-73e231a32c66.png", alt: "Chair, CURT Board of Directors" }, 
  { image: "/lovable-uploads/0cc2ee6a-c3fd-480c-b16c-ac8b36648245.png", alt: "CEO of CURT" }, 
  { image: "/gibniftys/pete-dumont.png", alt: "Pete Dumont" }
];

const CurtChannel = () => {
  const navigate = useNavigate();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  
  // Get live CURT network stats
  const { data: curtNetworkStats, isLoading } = useCurtNetworkStats();
  
  // Fetch CURT channel info
  const { data: curtChannel } = useQuery({
    queryKey: ['curt-channel'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('channels')
        .select('id, slug, name')
        .eq('slug', 'curt')
        .single();
      
      if (error) throw error;
      return data;
    }
  });
  
  // Fetch CURT domains from database
  const { data: curtDomains } = useQuery({
    queryKey: ['curt-domains'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domains')
        .select(`
          id,
          name,
          symbol,
          description,
          channels!inner(slug, name)
        `)
        .eq('channels.slug', 'curt')
        .order('name');
      
      if (error) throw error;
      
      // Format domains for DomainElements component
      return data?.map(d => ({
        symbol: d.symbol || d.name,
        name: d.name,
        categories: d.description || '',
        overview: d.description || ''
      })) || [];
    }
  });
  
  // Fetch knowledge bases with domain associations
  const { data: domainKBs } = useQuery({
    queryKey: ['curt-channel-kbs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('knowledge_base_domains')
        .select(`
          knowledge_base_id,
          domain_id,
          domains(name, symbol),
          knowledge_bases!inner(
            id,
            title,
            status,
            deleted_at,
            creator_id,
            channel_name
          )
        `)
        .eq('knowledge_bases.channel_name', 'CURT')
        .eq('knowledge_bases.status', 'published')
        .is('knowledge_bases.deleted_at', null);
      
      if (error) throw error;
      return data;
    }
  });


  return <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-3 flex items-center gap-6">
            <img src={curtLogo} alt="CURT Logo" className="w-24 h-24 object-contain cursor-pointer hover:opacity-80 transition-opacity" />
            <div>
              <h1 className="text-2xl font-bold mb-2">Construction Users Roundtable</h1>
              <p className="text-lg">
                CURT®: Improving Construction Around the World
              </p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2 space-y-6">
            <TeamMembersGrid members={curtTeamMembers} showAskCurt={true} useProfileAvatars={true} channelId="ec9b3b57-263a-4713-b3ed-5fe67808486e" />
            
            {isLoading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : curtNetworkStats ? (
              <KnowledgeNetworkStats {...curtNetworkStats} channelName="curt" />
            ) : (
              <div className="text-center p-12 text-muted-foreground">
                No data available
              </div>
            )}
          </div>
          <div className="lg:col-span-1">
            <CommandDemo channelName="CURT" />
          </div>
        </div>
        
        {/* Construction Domains - Full width, between grid and Industry Templates */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <h2 className="text-xl font-semibold mb-4">Construction Domains</h2>
            {curtDomains && curtDomains.length > 0 ? (
              <DomainElements domains={curtDomains} channelId={curtChannel?.id} />
            ) : (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Invite Dialog */}
        <InviteColleagueDialog
          open={inviteDialogOpen}
          onOpenChange={setInviteDialogOpen}
          defaultChannelId="ec9b3b57-263a-4713-b3ed-5fe67808486e"
          defaultChannelName="CURT"
        />
        <Card className="mb-6">
          <CardContent>
            <h2 className="text-xl font-semibold mb-4">Industry Templates</h2>
            <div className="grid grid-cols-3 gap-4">
              <div onClick={() => window.open('https://www.ispai.io/_files/ugd/2d66bb_aefbd8b72cdf4bba9426960e3f237731.pdf', '_blank')} className="bg-white border-2 border-black p-4 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <img src="/lovable-uploads/9943d1c9-6a3a-412a-8952-120fd044fb79.png" alt="Construction Industry Template" className="max-w-full h-auto mx-auto" />
              </div>
              <div onClick={() => window.open('https://www.ispai.io/_files/ugd/2d66bb_1dca9e81124a4d6fb00b6949cafc0303.pdf', '_blank')} className="bg-white border-2 border-black p-4 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <img src="/lovable-uploads/c394fc73-a823-469d-ba30-b61f806e5da9.png" alt="Construction Trades Industry Template" className="max-w-full h-auto mx-auto" />
              </div>
              <div onClick={() => window.open('https://www.ispai.io/_files/ugd/2d66bb_bf019caee4fd4fbb825db155296fa830.pdf', '_blank')} className="bg-white border-2 border-black p-4 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <img src="/lovable-uploads/15fbea79-bd2b-460b-b7b2-a42c7f93e89a.png" alt="Capital Projects Industry Template" className="max-w-full h-auto mx-auto" />
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>;
};
export default CurtChannel;
